/* eslint-disable @typescript-eslint/no-unused-expressions */
import type {CaseReducer, PayloadAction} from '@reduxjs/toolkit'

import type {PipelineId} from '../../../../../types'
import {generateFreeName} from '../../../../../utils/generateFreeName'

import type {PipelineDraftState} from './EditPipelinePage.slices.types'
import {deleteIfEmpty} from './EditPipelinePage.slices.utils'

export const setOutputParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    savedName: string
    value: string
  }>
> = (state, action) => {
  const {pipelineId, savedName, value} = action.payload
  const settings = state[pipelineId]?.draft?.settings

  if (settings != null) {
    settings['output-parameters'] ??= {}
    settings['output-parameters'][savedName] = value
  }
}

export const renameOutputParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    name: string
    savedName: string
  }>
> = (state, action) => {
  const {pipelineId, savedName, name} = action.payload

  if (name !== savedName) {
    state[pipelineId] ??= {}
    state[pipelineId]!.renamed ??= {}
    state[pipelineId]!.renamed!.outputParameters ??= {}
    state[pipelineId]!.renamed!.outputParameters![savedName] = name
  } else if (state[pipelineId]?.renamed?.outputParameters?.[savedName] != null) {
    delete state[pipelineId]!.renamed!.outputParameters![savedName]
    deleteIfEmpty(state[pipelineId]!.renamed, 'outputParameters') &&
      deleteIfEmpty(state[pipelineId], 'renamed')
  }
}

export const deleteOutputParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    savedName: string
  }>
> = (state, action) => {
  const {pipelineId, savedName} = action.payload
  const settings = state[pipelineId]?.draft?.settings

  if (state[pipelineId]?.added?.outputParameters?.[savedName]) {
    delete settings?.['output-parameters']?.[savedName]
    delete state[pipelineId]!.added!.outputParameters![savedName]
    deleteIfEmpty(state[pipelineId]!.added, 'outputParameters') &&
      deleteIfEmpty(state[pipelineId], 'added')
    delete state[pipelineId]!.renamed?.outputParameters?.[savedName]
    deleteIfEmpty(state[pipelineId]!.renamed, 'outputParameters') &&
      deleteIfEmpty(state[pipelineId], 'renamed')
  } else {
    state[pipelineId] ??= {}
    state[pipelineId]!.deleted ??= {}
    state[pipelineId]!.deleted!.outputParameters ??= []
    state[pipelineId]!.deleted!.outputParameters!.push(savedName)
  }
}

export const restoreOutputParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; savedName: string}>
> = (state, action) => {
  const {pipelineId, savedName} = action.payload
  const {deleted} = state[pipelineId] ?? {}

  if (deleted?.outputParameters?.includes(savedName)) {
    deleted.outputParameters = deleted?.outputParameters.filter(item => item !== savedName)
    deleteIfEmpty(deleted, 'outputParameters') && deleteIfEmpty(state[pipelineId], 'deleted')
  }
}

export const addOutputParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId}>
> = (state, action) => {
  const {pipelineId} = action.payload
  const settings = state[pipelineId]?.draft?.settings

  if (settings != null) {
    settings['output-parameters'] ??= {}
    const key = generateFreeName({
      getName: iteration => `output_parameter_${iteration}`,
      usedNames: Object.keys(settings['output-parameters']),
      errorMessage: 'Cannot generate unique parameter key',
    })
    settings['output-parameters'][key] = ''
    renameOutputParameter(state, {
      type: '',
      payload: {pipelineId, name: '', savedName: key},
    })
    state[pipelineId]!.added ??= {}
    state[pipelineId]!.added!.outputParameters ??= {}
    state[pipelineId]!.added!.outputParameters![key] = true
  }
}

export const clearAddedOutputParameters: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId}>
> = (state, action) => {
  const {pipelineId} = action.payload
  const {added} = state[pipelineId] ?? {}
  const settings = state[pipelineId]?.draft?.settings

  if (added?.outputParameters != null && settings != null) {
    Object.keys(added.outputParameters).forEach(key => {
      delete settings['output-parameters']?.[key]
    })
    delete added.outputParameters
    deleteIfEmpty(state[pipelineId], 'added')
  }
}

export const applyAddedOutputParameters: CaseReducer<PipelineDraftState, PayloadAction<string>> = (
  state,
  action,
) => {
  const pipelineId = action.payload
  const {added} = state[pipelineId] ?? {}

  if (added?.outputParameters != null) {
    delete added.outputParameters
    deleteIfEmpty(state[pipelineId], 'added')
  }
}
